import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Empty, Input, Message, Modal, Spin, Typography } from "@arco-design/web-react";
import { IconUpload } from "@arco-design/web-react/icon";
import { FetchApi, UploadFileApi } from "../Api/AuthenticatedApi";
import { confirmUpload, uploadFields } from "../FileAssets/shrinkNotice";

const { Text } = Typography;

const formatBytes = (n) => {
    if (!n) return "";
    if (n < 1024) return n + " B";
    if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
    return (n / (1024 * 1024)).toFixed(1) + " MB";
};

const extension = (name) => {
    const m = /\.([A-Za-z0-9]{1,5})$/.exec(name || "");
    return m ? m[1].toUpperCase() : "FILE";
};

// Picks a file from the org's file assets (Files), or uploads a new one into
// Files and picks that. onSelect(name) does the caller's work with it (publish
// an image, make it the incentive, …) and returns a promise; the tile spins
// until it settles, and the picker stays open if it fails.
export default function FileAssetPicker({ visible, onCancel, onSelect, title = "Choose a file", note, imagesOnly = false, accept }) {
    const { getAccessTokenSilently } = useAuth0();
    const [files, setFiles] = React.useState(null); // null = loading
    const [filter, setFilter] = React.useState("");
    const [busy, setBusy] = React.useState(""); // name being selected
    const [uploading, setUploading] = React.useState(false);
    const inputRef = React.useRef(null);

    const refresh = React.useCallback(() => {
        FetchApi(getAccessTokenSilently, "/api/files/",
            // preview_url is set only for images the browser can show.
            (data) => setFiles((data || []).filter((f) => !imagesOnly || !!f.preview_url)),
            () => setFiles([]),
            () => { setFiles([]); Message.error("Failed to load files"); });
    }, [getAccessTokenSilently, imagesOnly]);

    React.useEffect(() => {
        if (visible) {
            setFiles(null);
            setFilter("");
            refresh();
        }
    }, [visible, refresh]);

    const select = (name) => {
        setBusy(name);
        Promise.resolve(onSelect(name)).then(() => setBusy(""), () => setBusy(""));
    };

    const upload = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ""; // let the same file be picked again
        if (!file) return;
        // Large images are shrunk; ask first whether to keep the original.
        const choice = await confirmUpload([file]);
        if (!choice) return;
        if (files && files.some((f) => f.name === file.name)) {
            Message.warning(`"${file.name}" already exists in Files; using the new upload in its place.`);
        }
        setUploading(true);
        UploadFileApi(getAccessTokenSilently, "/api/files/",
            () => { setUploading(false); select(file.name); },
            () => setUploading(false),
            () => { setUploading(false); Message.error("Failed to upload the file"); },
            file,
            uploadFields(choice));
    };

    const shown = (files || []).filter((f) => !filter || f.name.toLowerCase().includes(filter.toLowerCase()));

    return (
        <Modal title={title} visible={visible} onCancel={onCancel} footer={null} style={{ width: 760, maxWidth: "calc(100vw - 32px)" }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <Input.Search allowClear placeholder="Filter by name" value={filter} onChange={setFilter} style={{ flex: 1 }} />
                <input ref={inputRef} type="file" accept={accept} style={{ display: "none" }} onChange={upload} />
                <Button type="primary" icon={<IconUpload />} loading={uploading} disabled={!!busy} onClick={() => inputRef.current && inputRef.current.click()}>
                    Upload new
                </Button>
            </div>
            {note && <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>{note}</Text>}

            {files === null ? (
                <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
            ) : shown.length === 0 ? (
                <Empty description={files.length ? "No files match" : `No ${imagesOnly ? "images" : "files"} in Files yet — upload one`} />
            ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12, maxHeight: "60vh", overflowY: "auto" }}>
                    {shown.map((f) => (
                        <button
                            key={f.name}
                            type="button"
                            disabled={!!busy}
                            onClick={() => select(f.name)}
                            title={f.name}
                            style={{
                                padding: 0, textAlign: "left", cursor: busy ? "wait" : "pointer",
                                border: "1px solid var(--color-border-2)", borderRadius: 6, overflow: "hidden",
                                background: "var(--color-bg-2)", opacity: busy && busy !== f.name ? 0.5 : 1,
                            }}
                        >
                            <div style={{ aspectRatio: "4 / 3", background: "var(--color-fill-2)", position: "relative", display: "grid", placeItems: "center" }}>
                                {f.preview_url ? (
                                    <img src={f.thumb_url || f.preview_url} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                                ) : (
                                    <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: "0.04em", color: "var(--color-text-3)" }}>{extension(f.name)}</span>
                                )}
                                {busy === f.name && (
                                    <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(255,255,255,.6)" }}><Spin /></div>
                                )}
                            </div>
                            <div style={{ padding: "6px 8px", fontSize: 12, color: "var(--color-text-2)" }}>
                                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</div>
                                {!!f.size && <div style={{ color: "var(--color-text-3)" }}>{formatBytes(f.size)}</div>}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </Modal>
    );
}
